<script setup>
import { onUpdated, defineProps, ref, watch } from "vue"
import { KNOLIA } from "@/knolia"
import { Gems } from "@/gems"
import RatingCircle from "../RatingCircle.vue"
import RatingScale from "../RatingScale.vue"
import ValidationTypeModal from "../ValidationTypeModal.vue"

const props = defineProps({
  ratingData: {
    type: Object,
    default: Object
  },

  ratingSkillsModal: {
    type: Object,
    default: Object
  }
})

const selectedElement = ref([])
const showValidationTypeModal = ref(false)
let showAll = ref(false)

onUpdated(() => {
  for (let skill in props.ratingData.rankList) {

    const sitemap = document.getElementById(`skill${skill}`)
    if(sitemap)
      Gems.Tooltip(sitemap, props.ratingData.rankList[skill].sitemap, "tooltip tooltip-left")
  }
});

watch(showAll, (e) => {
  if (e)
    document.querySelectorAll(".bi-chevron-down").forEach(elem => elem.click())
  else
    document.querySelectorAll(".bi-chevron-up").forEach(elem => elem.click())
})
</script>

<template>
  <div class="rank-table">
    <div class="show-all-container cursor-pointer text-end" @click="showAll = !showAll">
      <span>Expandir/Fechar tudo</span>
      <i class="icon fas" :class="{ 'fa-plus-square': !showAll, 'fa-minus-square': showAll }" />
    </div>

    <div v-for="(element, index) in props.ratingData.rankList.value" :key="element" class="rank-grid"
      :class="{'bg-alt-line': index % 2 == 0, 'highlighted': element.highlight}">
      <div class="row p-1 w-100 m-0">
        <span class="td-desig col-9 p-0 row m-0">
          <div v-if="element.showDetail" class="div-highlight-row col-auto p-0" @mouseenter="Gems.Tooltip($event.target, 'Destacar', 'tooltip')">
            <i class="fa-solid fa-highlighter" @click="props.ratingData.ChangeHighlight(element)"></i>
          </div>

          <i v-if="!element.is_op_skill" class="fa-solid fa-copyright col-auto p-0" @mouseover="props.ratingData.ManageTooltip($event.target, element)"></i>

          <span :id="`skill${index}`" class="line-clamp line-clamp-1 col-10 p-0 fw-bold" @mouseover="Gems.TooltipIfNeeded($event.target)"> {{ element.designation }} </span>
        </span>

        <span class="row col-3 p-0">
          <div class="col-6 span-circle fw-bold">
            <RatingCircle :txt="element.rating ?? '--'" />
          </div>
          
          <i class="bi bi-chevron-down col-6 p-0 align-content-center" :class="{'bi-chevron-up': element.showDetail}" @click="element.showDetail = !element.showDetail"></i>
        </span>
      </div>

      <div v-show="element.showDetail">
        <span class="radios p-1">
          <!-- <div v-for="i in 6" :key="i" :class="element.rating >= i ? `opt-color-${i}` : ''" :id="`opt-${i}`" @mouseenter="props.ratingData.CheckForTooltip($event, i, element)">
            <input :class="{'cursor-pointer': !KNOLIA.ToBoolean(element.waiting_validation)}" type="radio" 
                v-model="element.rating" :value="i" :disabled="KNOLIA.ToBoolean(element.waiting_validation) && element.rating != i"
                @click="props.ratingData.SetRating(element, i)">
            <label for="">{{ i }}</label>
          </div> -->
          <RatingScale :rating-data="props.ratingData" :element="element" :mobile-view="true"/>
        </span>

        <div class="row p-1 w-100 m-0">
          <span class="line-clamp line-clamp-1 col-9 text-start p-0" :id="`validation${index}`" :class="KNOLIA.ToBoolean(element.waiting_validation) ? 'temporary' : ''"  
            @mouseenter="Gems.Tooltip($event.target, props.ratingData.validationTooltipText(element), 'tooltip')">
            {{ element.waiting_validation ? 'Declarado' : element.current_validation_designation }} {{ props.ratingData.GetLastRatingTxt(element) }}
          </span>
          
          <span class="row col-3 p-0">
            <span class="col-6 p-0">
              <i class="fa-eye fas" @click="props.ratingSkillsModal.OpenModalDocs(element)"  @mouseenter="Gems.Tooltip($event.target, 'Anexar e Comentários', 'tooltip')"></i>
              <i class="fas fa-envelope" v-if="element.has_unread_messages" @mouseenter="Gems.Tooltip($event.target, 'Tens novas mensagens', 'tooltip')"></i>
            </span>
            <span class="col-6 p-0" v-show="element.rating != null">
              <i class="bi bi-send-x" v-if="!KNOLIA.ToBoolean(element.waiting_validation) && (element.current_validation != 4 || element.rating != element.current_rating)"
                @click="selectedElement = element; showValidationTypeModal = true"></i>
            </span>
          </span>
        </div>
      </div>
    </div>
  </div>

  <ValidationTypeModal :rating-data="props.ratingData" :show-validation-type-modal="showValidationTypeModal" :element="selectedElement" @close-modal="showValidationTypeModal = false"/>  
</template>

<style scoped>
@import '../assets/styles/rating-skills.css';

.rank-table {
  margin: 0 auto;
  width: 102%;
  margin-left: -1%;
  text-align: center;
}

.rank-grid-header {
  font-weight: bold;
}

.rank-grid {
  border-left: 4px solid transparent;
}

.highlighted {
  border-left-color: var(--knolia-yellow-white);
  color: var(--knolia-yellow-white);
}

.highlighted .span-circle .div-circle {
  background-color: var(--knolia-yellow-white);
}

.div-highlight-row {
  width: 10px;
}

.rank-table tbody td button {
  height: 28px;
  line-height: 1px;
}

.td-desig {
  text-align: start;
  gap: 5px;
  align-items: center;
}

.temporary {
  font-weight: 600;
  cursor: default;
}

.show-all-container i {
  margin-left: 3%;
  margin-right: 3%;
}

.show-all-container:hover {
  color: var(--default-hover-color);
}

.radios div {
  display: inline-flex;
  align-items: center;
  background-color: #143264;
  padding: 2px;
  flex: 1;
  justify-content: center;
}

.radios {
  display: flex;
}

.span-circle {
  text-align: -webkit-center;
}

.fa-copyright {
  font-size: 1.2em;
}

.fa-envelope { 
  position: relative;
  left: 0px;
  top: -6px;
  font-size: 0.7em !important;
  width: 0px;
}

</style>